import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from '@/components/ui/sonner';
import { useExperience, ALT, phaseIndex } from '@/store/experience';
import { detectQuality, supportsWebGL, QUALITY } from '@/lib/quality';
import { generateCloudAtlas } from '@/three/cloudTexture';
import { runCinematic } from '@/lib/cinematic';
import { SkyScene } from '@/three/SkyScene';
import { Fallback2D } from '@/components/Fallback2D';
import { LoadingScreen } from '@/components/LoadingScreen';
import { OpeningSequence } from '@/components/OpeningSequence';
import { Website } from '@/components/Website';
import { FloatingNavigation } from '@/components/FloatingNavigation';
import { RadioStation } from '@/components/RadioStation';
import { CustomCursor } from '@/components/CustomCursor';
import { FogOverlay } from '@/components/FogOverlay';

const preloadImage = (src) => new Promise((res) => { const i = new Image(); i.onload = i.onerror = res; i.src = src; });

export default function Experience() {
  const phase = useExperience((s) => s.phase);
  const webgl = useExperience((s) => s.webgl);
  const cloudCanvas = useExperience((s) => s.cloudCanvas);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    const st = useExperience.getState();
    const q = detectQuality();
    st.setQuality(q);
    st.setWebgl(supportsWebGL());

    const video = document.createElement('video');
    const h264 = video.canPlayType('video/mp4; codecs="avc1.42E01E"');
    const src = h264 ? '/assets/coin_matte.mp4' : '/assets/coin_matte.webm';
    Object.assign(video, { src, loop: true, muted: true, playsInline: true, preload: 'auto', crossOrigin: 'anonymous' });
    video.setAttribute('playsinline', '');
    videoRef.current = video;
    window.__coinVideo = video;

    let done = 0;
    const total = 5;
    const tick = () => { done += 1; setProgress(done / total); };
    const videoReady = new Promise((res) => { video.addEventListener('canplaythrough', res, { once: true }); video.addEventListener('error', res, { once: true }); setTimeout(res, 6000); video.load(); });

    (async () => {
      const start = performance.now();
      await Promise.all([
        new Promise((res) => { const v = document.createElement('video'); v.src = '/assets/coin.mp4'; v.preload = 'auto'; v.muted = true; v.addEventListener('canplaythrough', res, { once: true }); v.addEventListener('error', res, { once: true }); setTimeout(res, 6000); v.load(); }).then(tick),
        preloadImage('/assets/logo.png').then(tick),
        preloadImage('/assets/skydiver.png').then(tick),
        new Promise((r) => setTimeout(r, 30)).then(() => { st.setCloudCanvas(generateCloudAtlas(QUALITY[q].tex)); tick(); }),
        videoReady.then(tick),
      ]);
      const remaining = Math.max(0, 2200 - (performance.now() - start));
      await new Promise((r) => setTimeout(r, remaining));
      st.setPhase('AIRPLANE');
      setTimeout(() => useExperience.getState().setPhase('PASSWORD'), 1400);
    })();
  }, []);

  // Pointer → normalized device coords for camera parallax & cloud physics
  useEffect(() => {
    const frame = useExperience.getState().frame;
    const move = (x, y) => {
      frame.pointer.x = (x / window.innerWidth) * 2 - 1;
      frame.pointer.y = -(y / window.innerHeight) * 2 + 1;
      frame.pointerStrength = 1;
    };
    const onMove = (e) => move(e.clientX, e.clientY);
    const onTouch = (e) => { const t = e.touches[0]; if (t) move(t.clientX, t.clientY); };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('touchmove', onTouch, { passive: true });
    const decay = setInterval(() => { frame.pointerStrength = Math.max(0, frame.pointerStrength - 0.08); }, 60);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('touchmove', onTouch); clearInterval(decay); };
  }, []);

  useEffect(() => {
    if (phase === 'JUMPING') runCinematic(useExperience, { reduced: useExperience.getState().reducedMotion });
  }, [phase]);

  useEffect(() => {
    document.body.style.overflow = phaseIndex(phase) >= phaseIndex('WEBSITE') ? '' : 'hidden';
  }, [phase]);

  const showWebsite = phaseIndex(phase) >= phaseIndex('COIN_REVEAL');

  return (
    <div className="relative grain" data-testid="experience-root" data-phase={phase}>
      {webgl && cloudCanvas ? <SkyScene cloudCanvas={cloudCanvas} video={videoRef.current} /> : <Fallback2D cloudCanvas={cloudCanvas} />}
      <FogOverlay />
      <AnimatePresence>{phase === 'LOADING' && <LoadingScreen key="loading" progress={progress} />}</AnimatePresence>
      <OpeningSequence />
      {showWebsite && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: phase === 'COIN_REVEAL' ? 0 : 1 }} transition={{ duration: 1.6, ease: 'easeOut' }} style={{ pointerEvents: phase === 'COIN_REVEAL' ? 'none' : 'auto' }}>
          <Website />
        </motion.div>
      )}
      <FloatingNavigation />
      <RadioStation />
      <CustomCursor />
      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
}

export { ALT };
